import { useEffect, useMemo, useState } from 'react'
import type { MeasurementType, MeasurementTypeParameter, MeasurementTypeUnit } from '../../data/measurementTypes.ts'
import { type UserPreferences, usePreferences } from '../../data/userProfile.ts'
import type { XcastGraphData, XcastKey } from '../../data/xcast.ts'

export const useConvertMeasurement = () => {
	const [formatFn, setFormatFn] = useState(generateConvertFn(undefined))
	const { data: userPreferences } = usePreferences()

	useEffect(() => {
		setFormatFn(generateConvertFn(userPreferences))
	}, [userPreferences])

	return formatFn
}

const generateConvertFn = (userPreference: UserPreferences | undefined) => {
	return () =>
		<T extends Pick<MeasurementType, 'parameter' | 'unit'>>(
			value: number | undefined,
			measurementType: T | undefined,
			toTypeOverride?: MeasurementTypeUnit,
		) =>
			convertMeasurement(value, measurementType, userPreference, toTypeOverride)
}

const normalizeRainUnit = (userPreferredUnit: MeasurementTypeUnit, originalUnit: MeasurementTypeUnit): MeasurementTypeUnit => {
	// If original unit is "per hour" variant, ensure preferred unit is also "per hour"
	if (originalUnit === 'MILLIMETRES_PER_HOUR' || originalUnit === 'INCHES_PER_HOUR') {
		if (userPreferredUnit === 'INCHES') return 'INCHES_PER_HOUR'
		if (userPreferredUnit === 'MILLIMETRES') return 'MILLIMETRES_PER_HOUR'
	}
	return userPreferredUnit
}

const convertMeasurement = <T extends Pick<MeasurementType, 'parameter' | 'unit'>>(
	value: number | undefined,
	measurementType: T | undefined,
	userPreference: UserPreferences | undefined,
	toTypeOverride?: MeasurementTypeUnit,
): [number | undefined, T | undefined] => {
	const newMeasurementType = structuredClone(measurementType)
	// Check that we have a valid measurementType
	if (!newMeasurementType || !measurementType) {
		return [value, newMeasurementType]
	}

	// Check that the parameter is one that can be converted and the user preferences are loaded
	const parameter = newMeasurementType.parameter
	if (!(parameter in measurementParameterToUserPreference) || !userPreference) {
		return [value, newMeasurementType]
	}
	const category = measurementParameterToUserPreference[parameter as keyof typeof measurementParameterToUserPreference]
	// Get the preferred unit and normalize it for rain intensity/accumulation (handle _PER_HOUR variants)
	let userPreferredUnit = toTypeOverride || userPreference[category]
	userPreferredUnit = normalizeRainUnit(userPreferredUnit, newMeasurementType.unit)

	// Check that the preferred unit is different than the default unit
	if (userPreferredUnit === newMeasurementType.unit) {
		return [value, newMeasurementType]
	}

	const conversionFn = getConversionFunction(newMeasurementType.parameter, userPreferredUnit, measurementType.unit)
	if (!conversionFn) {
		return [value, newMeasurementType]
	}

	// Update the unit to the converted unit
	newMeasurementType.unit = userPreferredUnit

	// Check that value exists
	if (value === undefined) {
		return [value, newMeasurementType]
	}

	return [conversionFn(value), newMeasurementType]
}

type ForecastValue = number | [number, number] | undefined
export const useConvertForecastedMeasurement = () => {
	const { data: userPreferences } = usePreferences()
	return useMemo(
		() =>
			<T extends ForecastValue>(value: T, forecastParameter: keyof XcastGraphData[number]) =>
				convertForecastedMeasurements(value, forecastParameter, userPreferences),
		[userPreferences],
	)
}

type ForecastKeyConfig =
	| { kind: 'fixed'; unit: MeasurementTypeUnit }
	| { kind: 'passthrough'; getUnit: (prefs: UserPreferences) => MeasurementTypeUnit }
	| {
			kind: 'convert'
			parameter: MeasurementTypeParameter
			unitFrom: MeasurementTypeUnit
			getUnit: (prefs: UserPreferences) => MeasurementTypeUnit
	  }

const forecastKeyUnitMap = {
	WIND_DIRECTION_FORECAST: { kind: 'fixed', unit: 'DEGREES' },
	AIR_TEMPERATURE_FORECAST: { kind: 'passthrough', getUnit: (p) => p.unitsAirTemperature },
	WIND_SPEED_FORECAST: { kind: 'passthrough', getUnit: (p) => p.unitsWindSpeed },
	WIND_GUST_SPEED_FORECAST: { kind: 'passthrough', getUnit: (p) => p.unitsWindSpeed },
	AIR_TEMPERATURE_90: { kind: 'convert', parameter: 'AIR_TEMPERATURE', unitFrom: 'DEGREES_CELSIUS', getUnit: (p) => p.unitsAirTemperature },
	AIR_TEMPERATURE_50: { kind: 'convert', parameter: 'AIR_TEMPERATURE', unitFrom: 'DEGREES_CELSIUS', getUnit: (p) => p.unitsAirTemperature },
	WIND_SPEED_80: { kind: 'convert', parameter: 'WIND_SPEED', unitFrom: 'METRES_PER_SECOND', getUnit: (p) => p.unitsWindSpeed },
	WIND_SPEED_50: { kind: 'convert', parameter: 'WIND_SPEED', unitFrom: 'METRES_PER_SECOND', getUnit: (p) => p.unitsWindSpeed },
} satisfies Record<XcastKey, ForecastKeyConfig>

const applyConversionFn = <T extends ForecastValue>(value: T, fn: (v: number) => number): T => {
	if (value === undefined) return value
	if (Array.isArray(value)) return value.map(fn) as T
	return fn(value) as T
}

const convertForecastedMeasurements = <T extends ForecastValue>(
	value: T,
	forecastParameter: keyof XcastGraphData[number],
	userPreference: UserPreferences | undefined,
): [T, MeasurementTypeUnit | undefined] => {
	if (!userPreference || forecastParameter === 'timestamp') {
		return [value, undefined]
	}

	const config = forecastKeyUnitMap[forecastParameter]
	if (!config) return [value, undefined]

	if (config.kind === 'fixed') return [value, config.unit]
	if (config.kind === 'passthrough') return [value, config.getUnit(userPreference)]

	const preferredUnit = config.getUnit(userPreference)
	if (value === undefined || preferredUnit === config.unitFrom) return [value, preferredUnit]

	const conversionFn = getConversionFunction(config.parameter, preferredUnit, config.unitFrom)
	if (!conversionFn) return [value, undefined]

	return [applyConversionFn(value, conversionFn), preferredUnit]
}

const celsiusToFahrenheit = (value: number) => {
	return value * 1.8 + 32
}

const fahrenheitToCelsius = (value: number) => {
	return ((value - 32) * 5) / 9
}

const msToMph = (value: number) => {
	return value * 2.237
}

const msToKnots = (value: number) => {
	return value * 1.944
}

const msToKmh = (value: number) => {
	return value * 3.6
}

const mphToMs = (value: number) => {
	return value / 2.237
}

const knotsToMs = (value: number) => {
	return value / 1.944
}

const kmhToMs = (value: number) => {
	return value / 3.6
}

const hpaToMbar = (value: number) => {
	return value
}

const hpaToInHg = (value: number) => {
	return value / 33.864
}

const inHgToHpa = (value: number) => {
	return value * 33.864
}

const inchToMeter = (value: number) => {
	return 0.0254 * value
}

const mmToInches = (value: number) => {
	return value / 25.4
}

const inchesToMm = (value: number) => {
	return value * 25.4
}

const ppbToPpm = (value: number) => {
	return value / 1000
}

const ppmToPpb = (value: number) => {
	return value * 1000
}

const ppbToMgm3 = (value: number, factor: number) => {
	return value * factor
}

const mgm3ToPpb = (value: number, factor: number) => {
	return value * (1 / factor)
}

const kmToM = (value: number) => {
	return value * 1000
}

const kmToMi = (value: number) => {
	return value / 1.609_34
}

const kmToNmi = (value: number) => {
	return value / 1.852
}

const mToKm = (value: number) => {
	return value / 1000
}

const mToMi = (value: number) => {
	return value / 1609.34
}

const mToIn = (value: number) => {
	return value * 39.37
}

const mToCm = (value: number) => {
	return value * 100
}

const mToNmi = (value: number) => {
	return value / 1852
}

const miToKm = (value: number) => {
	return value * 1.609_34
}

const miToM = (value: number) => {
	return value * 1609.34
}

const miToNmi = (value: number) => {
	return value * 0.868_976
}

const nmiToKm = (value: number) => {
	return value * 1.852
}

const nmiToM = (value: number) => {
	return value * 1852
}

const nmiToMi = (value: number) => {
	return value * 1.150_78
}

const perKmSqToPerMiSq = (value: number) => {
	return value * 2.589_99
}

const perKmSqToPerNmiSq = (value: number) => {
	return value * 3.429_904
}

const kmSqrToMiSqr = (value: number) => {
	return value * 0.386_102
}

const kmSqrToNmiSqr = (value: number) => {
	return value * 0.291_553
}

const miSqrToKmSqr = (value: number) => {
	return value * 2.589_99
}

const miSqrToNmiSqr = (value: number) => {
	return value / 1.324
}

const nmiSqrToKmSqr = (value: number) => {
	return value * 3.429_904
}

const nmiSqrToMiSqr = (value: number) => {
	return value * 1.324
}

const gasConversionFactors = {
	NO2_GAS_CONCENTRATION: 1.912,
	SO2_GAS_CONCENTRATION: 2.66,
	CO_GAS_CONCENTRATION: 1.16,
	H2S_GAS_CONCENTRATION: 1.417,
	O3_GAS_CONCENTRATION: 2.0,
	NO_GAS_CONCENTRATION: 1.247,
}

// Map structure: [FROM_UNIT][TO_UNIT] => conversion function
// Example: temperatureConversionMap['DEGREES_CELSIUS']['DEGREES_FAHRENHEIT'] returns celsiusToFahrenheit
type ConversionMap<T extends string> = Record<T, Record<T, (value: number) => number>>

const distanceConversionMap: ConversionMap<'KILOMETRES' | 'METRES' | 'MILES' | 'NAUTICAL_MILES'> = {
	KILOMETRES: {
		METRES: kmToM,
		MILES: kmToMi,
		NAUTICAL_MILES: kmToNmi,
		KILOMETRES: (value: number) => value,
	},
	METRES: {
		KILOMETRES: mToKm,
		MILES: mToMi,
		NAUTICAL_MILES: mToNmi,
		METRES: (value: number) => value,
	},
	MILES: {
		KILOMETRES: miToKm,
		METRES: miToM,
		NAUTICAL_MILES: miToNmi,
		MILES: (value: number) => value,
	},
	NAUTICAL_MILES: {
		KILOMETRES: nmiToKm,
		METRES: nmiToM,
		MILES: nmiToMi,
		NAUTICAL_MILES: (value: number) => value,
	},
}

const isValidDistanceUnit = (unit: MeasurementTypeUnit): unit is keyof typeof distanceConversionMap => {
	return unit in distanceConversionMap
}

const areaConversionMap: ConversionMap<'KILOMETRES_SQUARED' | 'MILES_SQUARED' | 'NAUTICAL_MILES_SQUARED'> = {
	KILOMETRES_SQUARED: {
		MILES_SQUARED: kmSqrToMiSqr,
		NAUTICAL_MILES_SQUARED: kmSqrToNmiSqr,
		KILOMETRES_SQUARED: (value: number) => value,
	},
	MILES_SQUARED: {
		KILOMETRES_SQUARED: miSqrToKmSqr,
		NAUTICAL_MILES_SQUARED: miSqrToNmiSqr,
		MILES_SQUARED: (value: number) => value,
	},
	NAUTICAL_MILES_SQUARED: {
		KILOMETRES_SQUARED: nmiSqrToKmSqr,
		MILES_SQUARED: nmiSqrToMiSqr,
		NAUTICAL_MILES_SQUARED: (value: number) => value,
	},
}

const isValidAreaUnit = (unit: MeasurementTypeUnit): unit is keyof typeof areaConversionMap => {
	return unit in areaConversionMap
}

const temperatureConversionMap: ConversionMap<'DEGREES_CELSIUS' | 'DEGREES_FAHRENHEIT'> = {
	DEGREES_CELSIUS: {
		DEGREES_FAHRENHEIT: celsiusToFahrenheit,
		DEGREES_CELSIUS: (value: number) => value,
	},
	DEGREES_FAHRENHEIT: {
		DEGREES_CELSIUS: fahrenheitToCelsius,
		DEGREES_FAHRENHEIT: (value: number) => value,
	},
}

const isValidTemperatureUnit = (unit: MeasurementTypeUnit): unit is keyof typeof temperatureConversionMap => {
	return unit in temperatureConversionMap
}

const windSpeedConversionMap: ConversionMap<'METRES_PER_SECOND' | 'MILES_PER_HOUR' | 'KNOTS' | 'KILOMETRES_PER_HOUR'> = {
	METRES_PER_SECOND: {
		MILES_PER_HOUR: msToMph,
		KNOTS: msToKnots,
		KILOMETRES_PER_HOUR: msToKmh,
		METRES_PER_SECOND: (value: number) => value,
	},
	MILES_PER_HOUR: {
		METRES_PER_SECOND: mphToMs,
		KNOTS: (value: number) => msToKnots(mphToMs(value)),
		KILOMETRES_PER_HOUR: (value: number) => msToKmh(mphToMs(value)),
		MILES_PER_HOUR: (value: number) => value,
	},
	KNOTS: {
		METRES_PER_SECOND: knotsToMs,
		MILES_PER_HOUR: (value: number) => msToMph(knotsToMs(value)),
		KILOMETRES_PER_HOUR: (value: number) => msToKmh(knotsToMs(value)),
		KNOTS: (value: number) => value,
	},
	KILOMETRES_PER_HOUR: {
		METRES_PER_SECOND: kmhToMs,
		MILES_PER_HOUR: (value: number) => msToMph(kmhToMs(value)),
		KNOTS: (value: number) => msToKnots(kmhToMs(value)),
		KILOMETRES_PER_HOUR: (value: number) => value,
	},
}

const isValidWindSpeedUnit = (unit: MeasurementTypeUnit): unit is keyof typeof windSpeedConversionMap => {
	return unit in windSpeedConversionMap
}

const airPressureConversionMap: ConversionMap<'HECTOPASCALS' | 'MILLIBARS' | 'INCH_OF_MERCURY'> = {
	HECTOPASCALS: {
		MILLIBARS: hpaToMbar,
		INCH_OF_MERCURY: hpaToInHg,
		HECTOPASCALS: (value: number) => value,
	},
	MILLIBARS: {
		HECTOPASCALS: hpaToMbar,
		INCH_OF_MERCURY: hpaToInHg,
		MILLIBARS: (value: number) => value,
	},
	INCH_OF_MERCURY: {
		HECTOPASCALS: inHgToHpa,
		MILLIBARS: inHgToHpa,
		INCH_OF_MERCURY: (value: number) => value,
	},
}

const isValidAirPressureUnit = (unit: MeasurementTypeUnit): unit is keyof typeof airPressureConversionMap => {
	return unit in airPressureConversionMap
}

const rainConversionMap: ConversionMap<'MILLIMETRES' | 'MILLIMETRES_PER_HOUR' | 'INCHES' | 'INCHES_PER_HOUR'> = {
	MILLIMETRES: {
		INCHES: mmToInches,
		INCHES_PER_HOUR: mmToInches,
		MILLIMETRES: (value: number) => value,
		MILLIMETRES_PER_HOUR: (value: number) => value,
	},
	MILLIMETRES_PER_HOUR: {
		INCHES: mmToInches,
		INCHES_PER_HOUR: mmToInches,
		MILLIMETRES: (value: number) => value,
		MILLIMETRES_PER_HOUR: (value: number) => value,
	},
	INCHES: {
		MILLIMETRES: inchesToMm,
		MILLIMETRES_PER_HOUR: inchesToMm,
		INCHES: (value: number) => value,
		INCHES_PER_HOUR: (value: number) => value,
	},
	INCHES_PER_HOUR: {
		MILLIMETRES: inchesToMm,
		MILLIMETRES_PER_HOUR: inchesToMm,
		INCHES: (value: number) => value,
		INCHES_PER_HOUR: (value: number) => value,
	},
}

const isValidRainUnit = (unit: MeasurementTypeUnit): unit is keyof typeof rainConversionMap => {
	return unit in rainConversionMap
}

const snowDepthConversionMap: ConversionMap<'METRES' | 'INCHES' | 'CENTIMETRES'> = {
	METRES: {
		INCHES: mToIn,
		CENTIMETRES: mToCm,
		METRES: (value: number) => value,
	},
	INCHES: {
		METRES: inchToMeter,
		CENTIMETRES: (value: number) => mToCm(inchToMeter(value)),
		INCHES: (value: number) => value,
	},
	CENTIMETRES: {
		METRES: (value: number) => value / 100,
		INCHES: (value: number) => mToIn(value / 100),
		CENTIMETRES: (value: number) => value,
	},
}

const isValidSnowDepthUnit = (unit: MeasurementTypeUnit): unit is keyof typeof snowDepthConversionMap => {
	return unit in snowDepthConversionMap
}

const densityPerYearConversionMap: ConversionMap<
	| 'EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT'
	| 'EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT'
	| 'EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT'
> = {
	EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT: {
		EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT: perKmSqToPerMiSq,
		EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT: perKmSqToPerNmiSq,
		EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT: (value: number) => value,
	},
	EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT: {
		EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT: (value: number) => value / 2.589_99,
		EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT: (value: number) => perKmSqToPerNmiSq(value / 2.589_99),
		EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT: (value: number) => value,
	},
	EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT: {
		EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT: (value: number) => value / 3.429_904,
		EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT: (value: number) => perKmSqToPerMiSq(value / 3.429_904),
		EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT: (value: number) => value,
	},
}

const densityPerHourConversionMap: ConversionMap<
	| 'EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT'
	| 'EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT'
	| 'EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT'
> = {
	EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT: {
		EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT: perKmSqToPerMiSq,
		EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT: perKmSqToPerNmiSq,
		EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT: (value: number) => value,
	},
	EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT: {
		EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT: (value: number) => value / 2.589_99,
		EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT: (value: number) => perKmSqToPerNmiSq(value / 2.589_99),
		EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT: (value: number) => value,
	},
	EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT: {
		EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT: (value: number) => value / 3.429_904,
		EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT: (value: number) => perKmSqToPerMiSq(value / 3.429_904),
		EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT: (value: number) => value,
	},
}

type DensityUnit = keyof typeof densityPerYearConversionMap | keyof typeof densityPerHourConversionMap

const densityConversionMap: Record<DensityUnit, Partial<Record<DensityUnit, (value: number) => number>>> = {
	...densityPerYearConversionMap,
	...densityPerHourConversionMap,
}

const isValidDensityUnit = (unit: MeasurementTypeUnit): unit is DensityUnit => {
	return unit in densityConversionMap
}

const getTemperatureConversion = (unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (isValidTemperatureUnit(unitFrom) && isValidTemperatureUnit(unitTo)) {
		return temperatureConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getWindSpeedConversion = (unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (isValidWindSpeedUnit(unitFrom) && isValidWindSpeedUnit(unitTo)) {
		return windSpeedConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getAirPressureConversion = (unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (isValidAirPressureUnit(unitFrom) && isValidAirPressureUnit(unitTo)) {
		return airPressureConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getRainConversion = (parameterFrom: MeasurementTypeParameter, unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	const isRainParameter =
		parameterFrom === 'RAIN_INTENSITY' ||
		parameterFrom === 'RAIN_ACCUMULATION' ||
		parameterFrom === 'PRECIPITATION_INTENSITY' ||
		parameterFrom === 'PRECIPITATION_ACCUMULATION' ||
		parameterFrom === 'SNOW_INTENSITY' ||
		parameterFrom === 'SNOW_ACCUMULATION'

	if (isRainParameter && isValidRainUnit(unitFrom) && isValidRainUnit(unitTo)) {
		return rainConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getSnowDepthConversion = (parameterFrom: MeasurementTypeParameter, unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (parameterFrom === 'SNOW_DEPTH' && isValidSnowDepthUnit(unitFrom) && isValidSnowDepthUnit(unitTo)) {
		return snowDepthConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getGasConversion = (parameterFrom: MeasurementTypeParameter, unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (!(parameterFrom in gasConversionFactors)) {
		return undefined
	}

	const factor = gasConversionFactors[parameterFrom as keyof typeof gasConversionFactors]
	if (unitFrom === 'MICROGRAMS_PER_CUBIC_METRE' && unitTo === 'PARTS_PER_BILLION') return (value: number) => mgm3ToPpb(value, factor)
	if (unitFrom === 'PARTS_PER_MILLION' && unitTo === 'PARTS_PER_BILLION') return ppmToPpb
	if (unitFrom === 'PARTS_PER_BILLION' && unitTo === 'PARTS_PER_MILLION') return ppbToPpm
	if (unitFrom === 'PARTS_PER_BILLION' && unitTo === 'MICROGRAMS_PER_CUBIC_METRE') return (value: number) => ppbToMgm3(value, factor)
	return undefined
}

const getDistanceConversion = (parameterFrom: MeasurementTypeParameter, unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (parameterFrom === 'DISTANCE' && isValidDistanceUnit(unitFrom) && isValidDistanceUnit(unitTo)) {
		return distanceConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getAreaConversion = (parameterFrom: MeasurementTypeParameter, unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (parameterFrom === 'AREA' && isValidAreaUnit(unitFrom) && isValidAreaUnit(unitTo)) {
		return areaConversionMap[unitFrom][unitTo]
	}
	return undefined
}

const getDensityConversion = (parameterFrom: MeasurementTypeParameter, unitFrom: MeasurementTypeUnit, unitTo: MeasurementTypeUnit) => {
	if (parameterFrom === 'DENSITY' && isValidDensityUnit(unitFrom) && isValidDensityUnit(unitTo))
		return densityConversionMap[unitFrom][unitTo]
	return undefined
}

export const getConversionFunction = (
	parameterFrom: MeasurementTypeParameter,
	unitTo: MeasurementTypeUnit,
	unitFrom: MeasurementTypeUnit,
) => {
	return (
		getTemperatureConversion(unitFrom, unitTo) ||
		getWindSpeedConversion(unitFrom, unitTo) ||
		getAirPressureConversion(unitFrom, unitTo) ||
		getRainConversion(parameterFrom, unitFrom, unitTo) ||
		getSnowDepthConversion(parameterFrom, unitFrom, unitTo) ||
		getGasConversion(parameterFrom, unitFrom, unitTo) ||
		getDistanceConversion(parameterFrom, unitFrom, unitTo) ||
		getAreaConversion(parameterFrom, unitFrom, unitTo) ||
		getDensityConversion(parameterFrom, unitFrom, unitTo)
	)
}

const measurementParameterToUserPreference = {
	AIR_PRESSURE: 'unitsAirPressure',
	AIR_PRESSURE_QFE: 'unitsAirPressure',
	AIR_PRESSURE_QFF: 'unitsAirPressure',
	AIR_PRESSURE_QNH: 'unitsAirPressure',
	AIR_TEMPERATURE: 'unitsAirTemperature',
	CO_GAS_CONCENTRATION: 'unitsOtherGases',
	DEW_POINT_TEMPERATURE: 'unitsAirTemperature',
	FROST_POINT_TEMPERATURE: 'unitsAirTemperature',
	H2S_GAS_CONCENTRATION: 'unitsOtherGases',
	INTERNAL_TEMPERATURE: 'unitsAirTemperature',
	NO2_GAS_CONCENTRATION: 'unitsOtherGases',
	NO_GAS_CONCENTRATION: 'unitsOtherGases',
	O3_GAS_CONCENTRATION: 'unitsOtherGases',
	PRECIPITATION_ACCUMULATION: 'unitsRain',
	PRECIPITATION_INTENSITY: 'unitsRain',
	PV_PANEL_TEMPERATURE: 'unitsAirTemperature',
	RAIN_ACCUMULATION: 'unitsRain',
	RAIN_INTENSITY: 'unitsRain',
	SNOW_ACCUMULATION: 'unitsRain',
	SNOW_INTENSITY: 'unitsRain',
	SO2_GAS_CONCENTRATION: 'unitsOtherGases',
	SUB_SURFACE_TEMPERATURE: 'unitsAirTemperature',
	SUB_SURFACE_TEMPERATURE_BASE: 'unitsAirTemperature',
	SURFACE_TEMPERATURE: 'unitsAirTemperature',
	WIND_GUST_SPEED: 'unitsWindSpeed',
	WIND_SPEED: 'unitsWindSpeed',
} satisfies Partial<Record<MeasurementTypeParameter, UnitPreferenceCategory>>

type UnitPreferenceCategory = keyof Omit<UserPreferences, 'userId' | 'timezone' | 'language' | 'unitsAirQuality'>
